import { SEARCH_MODE_SUGGESTION_KEYWORDS } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/consts.js';
import { useIsModeVisible } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useIsModeVisible.js';
import { useSearchModeGateInfo } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useSearchModeGateInfo.js';
import { useUserInput } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { useEffect, useMemo, useRef } from 'react';
const getQueryToSearch = (query) => {
    return query.trim().toLowerCase().split(/\s+/).slice(-5).join(' ');
};
// Auto-apply the Search dropdown submode that matches the typed query.
// When the user is in plain SEARCH and types something matching e.g.
// "research", we promote it directly into the search toggle instead of
// leaving a pill for them to click.
//
// Once the user manually picks any submode (including SEARCH itself after
// we've auto-applied), auto-apply locks out for the rest of the search
// session — their pick wins over the suggestion. Entering Computer (ASI)
// mode resets the lockout, so returning to Search afterward re-enables
// auto-apply for the next query.
//
// Suggestions are computed independently of `selectedSearchMode` so that the
// auto-applied state is a fixed point: after we flip to RESEARCH, the top
// match stays RESEARCH and the effect doesn't oscillate.
export const useAutoApplyDifferentSearchMode = ({ enabled = true, selectedSearchMode, isFollowUp, onSearchModeSelect, omittedSearchModes = EMPTY_ARRAY, }) => {
    const query = useUserInput();
    const queryToSearch = getQueryToSearch(query);
    const isModeVisible = useIsModeVisible();
    const getSearchModeGateInfo = useSearchModeGateInfo();
    // Skip suggestions for modes the user can't currently use — auto-applying
    // a gated mode (e.g. RESEARCH for an unentitled user) drops them into a
    // broken state. The user can still see + click the gated row in the Search
    // dropdown to trigger the upsell.
    const topAutoApplyMode = useMemo(() => {
        for (const [searchMode, keywords] of SEARCH_MODE_SUGGESTION_KEYWORDS) {
            if (omittedSearchModes.includes(searchMode))
                continue;
            if (!isModeVisible(searchMode))
                continue;
            if (getSearchModeGateInfo(searchMode).isGated)
                continue;
            if (keywords.some((keyword) => queryToSearch.includes(keyword))) {
                return searchMode;
            }
        }
        return null;
    }, [queryToSearch, omittedSearchModes, isModeVisible, getSearchModeGateInfo]);
    const lastAutoAppliedRef = useRef(null);
    const autoApplyDisabledRef = useRef(false);
    useEffect(() => {
        if (!enabled) {
            lastAutoAppliedRef.current = null;
            autoApplyDisabledRef.current = false;
            return;
        }
        if (isFollowUp) {
            lastAutoAppliedRef.current = null;
            autoApplyDisabledRef.current = false;
            return;
        }
        // Entering Computer mode resets the lockout — the user can return to
        // Search afterward and have auto-apply re-engage.
        if (selectedSearchMode === SearchMode.ASI) {
            lastAutoAppliedRef.current = null;
            autoApplyDisabledRef.current = false;
            return;
        }
        // `lastAutoAppliedRef` holds the mode this hook last switched the user
        // into. When `selectedSearchMode` still matches it, the current mode is
        // ours to keep adjusting (swap to a different submode if a new one
        // starts matching).
        const autoAppliedCurrentMode = lastAutoAppliedRef.current !== null && selectedSearchMode === lastAutoAppliedRef.current;
        if (autoAppliedCurrentMode) {
            if (topAutoApplyMode !== null && topAutoApplyMode !== lastAutoAppliedRef.current) {
                lastAutoAppliedRef.current = topAutoApplyMode;
                onSearchModeSelect(topAutoApplyMode);
            }
            return;
        }
        // Current mode isn't ours. If we owned one, the user picked a submode
        // manually — lock out auto-apply until they pass through Computer mode.
        if (lastAutoAppliedRef.current !== null) {
            autoApplyDisabledRef.current = true;
        }
        lastAutoAppliedRef.current = null;
        if (autoApplyDisabledRef.current)
            return;
        if (selectedSearchMode === SearchMode.SEARCH && topAutoApplyMode !== null) {
            lastAutoAppliedRef.current = topAutoApplyMode;
            onSearchModeSelect(topAutoApplyMode);
        }
    }, [enabled, topAutoApplyMode, selectedSearchMode, isFollowUp, onSearchModeSelect]);
};
//# sourceMappingURL=useAutoApplyDifferentSearchMode.js.map